import * as React from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  List,
  ListItem,
  ListItemText,
  ListSubheader,
  Switch,
  Typography,
} from "@mui/material";
import { useMemo, useState } from "react";
import { useSendActivationEmails } from "../hooks/useSendActivationEmails";

export const ActivationEmailDialog = ({ open, setOpen, users }) => {
  const [includeInactive, setIncludeInactive] = useState(false);
  const { sendActivationEmails } = useSendActivationEmails();

  // Unverified users only
  const unverifiedUsers = useMemo(
    () => users.filter((u) => !u.isEmailVerified),
    [users],
  );

  const activeUsers = useMemo(
    () => unverifiedUsers.filter((u) => u.isActive),
    [unverifiedUsers],
  );

  const inactiveUsers = useMemo(
    () => unverifiedUsers.filter((u) => !u.isActive),
    [unverifiedUsers],
  );

  const usersToEmail = useMemo(() => {
    return includeInactive ? unverifiedUsers : activeUsers;
  }, [includeInactive, unverifiedUsers, activeUsers]);

  const sendEmails = async () => {
    const result = await sendActivationEmails(usersToEmail);
    if (result.success) {
      handleClose();
    }
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth={"md"}>
      <DialogTitle>Send verification emails</DialogTitle>
      <DialogContent>
        <FormControlLabel
          control={
            <Switch
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
              color="warning"
            />
          }
          label={`Include inactive users (${inactiveUsers.length})`}
        />

        {includeInactive && (
          <Alert severity="warning" sx={{ mt: 1 }}>
            Verification emails are normally only sent to{" "}
            <strong>active users</strong>. User activation is managed by{" "}
            <strong>Wonde</strong> and they are likely to be deactivated during
            the next sync.
          </Alert>
        )}
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle1" gutterBottom>
            Recipients ({usersToEmail.length})
          </Typography>

          <Box
            sx={{
              maxHeight: 300,
              overflowY: "auto",
              border: "1px solid",
              borderColor: "divider",
              borderRadius: 1,
            }}
          >
            <List dense>
              {activeUsers.length > 0 && (
                <>
                  <ListSubheader>Unverified (Active)</ListSubheader>
                  {activeUsers.map((u) => (
                    <ListItem key={u.id}>
                      <ListItemText
                        primary={`${u.firstName} ${u.lastName} (${u.email})`}
                        primaryTypographyProps={{ variant: "body1" }}
                      />
                    </ListItem>
                  ))}
                </>
              )}

              {includeInactive && inactiveUsers.length > 0 && (
                <>
                  <Divider sx={{ mt: 1 }} />
                  <ListSubheader sx={{ mt: 1 }}>
                    Unverified (Inactive)
                  </ListSubheader>
                  {inactiveUsers.map((u) => (
                    <ListItem key={u.id}>
                      <ListItemText
                        primary={`${u.firstName} ${u.lastName} (${u.email})`}
                      />
                    </ListItem>
                  ))}
                </>
              )}
            </List>
          </Box>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} variant={"outlined"}>
          Cancel
        </Button>
        <Button variant={"contained"} onClick={sendEmails}>
          Send
        </Button>
      </DialogActions>
    </Dialog>
  );
};
